import { Box, SettingsIcon, WarningIcon } from '@deckee/deck-hand';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import { selectCurrentMember } from '../../../../services/store/modules/backend-service';
import MemberAvatar from '../../../shared/_deprecated/components/MemberAvatar/MemberAvatar';
import {
  checklistFilled,
  checklistOutlined,
  closePane,
  customer,
  customerOutlined,
  deckeeWordmarkBlack,
  floatPlanMap,
  locationOutlined,
  locationOutlinedFilled,
  openPane,
  people,
  questionCircle,
  ReactSvg,
  weather,
  weatherFilled,
} from '../../../shared/vector-images';
import useAlertsCount from '../../Notifications/hooks/use-alerts-count';
import {
  SIDEBAR_AVATAR_SIZE,
  SIDEBAR_ICON_SIZE,
  SIDEBAR_LOGO_HEIGHT,
  SIDEBAR_LOGO_WIDTH,
  SIDEBAR_TOGGLE_RADIUS,
  SIDEBAR_TOGGLE_SIZE,
  SIDEBAR_TRANSITION,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from '../sidebarConstants';
import {
  MenuItemEnum,
  useActiveMenu,
  useNavigateToMenu,
} from '../useAppNavigation';
import SidebarItem from './SidebarItem';

const USER_GUIDE_URL = 'https://support.deckee.com/';

const SidebarWrapper = styled(Box).attrs({ as: 'nav' })`
  box-sizing: border-box;
  border-right: 1px solid ${({ theme }) => theme.colors.line};
  transition: width ${SIDEBAR_TRANSITION};
`;

const PaneToggleButton = styled(Box).attrs({ as: 'button', type: 'button' })`
  /* Button reset that leaves the Box props (size, radius) intact */
  appearance: none;
  border: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.lightgrey};
  }
`;

interface DesktopSidebarProps {
  collapsed: boolean;
  /** Focused map flows hide navigation; the items can't be used meanwhile */
  disabled?: boolean;
  onToggleCollapsed: () => void;
}

const DesktopSidebar: FC<DesktopSidebarProps> = ({
  collapsed,
  disabled = false,
  onToggleCollapsed,
}) => {
  const theme = useTheme();
  const member = useSelector(selectCurrentMember);
  const activeMenu = useActiveMenu({ desktopSections: true });
  const navigateToMenu = useNavigateToMenu();
  const alertsCount = useAlertsCount();

  const handleSelect = (menuName: MenuItemEnum) => () => {
    captureEvent({
      type: 'desktop-sidebar: select item',
      payload: { name: menuName },
      sendToAmplitude: true,
    });
    navigateToMenu(menuName);
  };

  const handleOpenUserGuide = () => {
    captureEvent({
      type: 'desktop-sidebar: open user guide',
      payload: {},
      sendToAmplitude: true,
    });
    // Opens a new window on web and the in-app browser on native tablets
    openWithInAppBrowser({ url: USER_GUIDE_URL });
  };

  const isActive = (menuName: MenuItemEnum) => activeMenu === menuName;
  const iconColor = (menuName: MenuItemEnum) =>
    isActive(menuName) ? theme.colors.primary : theme.colors.black;
  const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <SidebarWrapper
      aria-label="Main navigation"
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      width={collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED}
      display="flex"
      flexDirection="column"
      py={3}
      px={2.5}
      bg="white"
      overflow="hidden"
    >
      <Box
        height={SIDEBAR_TOGGLE_SIZE}
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent={collapsed ? 'center' : 'space-between'}
      >
        {!collapsed && (
          <Box pl={2.5} display="flex" alignItems="center">
            <ReactSvg
              toDisplay={deckeeWordmarkBlack()}
              options={{ fillColor: theme.colors.primary }}
              width={SIDEBAR_LOGO_WIDTH}
              height={SIDEBAR_LOGO_HEIGHT}
            />
          </Box>
        )}
        <PaneToggleButton
          onClick={onToggleCollapsed}
          aria-label={toggleLabel}
          aria-expanded={!collapsed}
          title={toggleLabel}
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          width={SIDEBAR_TOGGLE_SIZE}
          height={SIDEBAR_TOGGLE_SIZE}
          borderRadius={SIDEBAR_TOGGLE_RADIUS}
        >
          <ReactSvg
            toDisplay={collapsed ? closePane() : openPane()}
            options={{ fillColor: theme.colors.black }}
            width={SIDEBAR_ICON_SIZE}
            height={SIDEBAR_ICON_SIZE}
          />
        </PaneToggleButton>
      </Box>

      <Box display="flex" flexDirection="column">
        <SidebarItem
          label="Explore"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.PLAN)}
          onClick={handleSelect(MenuItemEnum.PLAN)}
          icon={
            isActive(MenuItemEnum.PLAN)
              ? locationOutlinedFilled()
              : locationOutlined()
          }
        />
        <SidebarItem
          label="Prepare"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.PREPARE)}
          onClick={handleSelect(MenuItemEnum.PREPARE)}
          icon={
            isActive(MenuItemEnum.PREPARE)
              ? checklistFilled()
              : checklistOutlined()
          }
        />
        <SidebarItem
          label="Weather"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.WEATHER)}
          onClick={handleSelect(MenuItemEnum.WEATHER)}
          icon={isActive(MenuItemEnum.WEATHER) ? weatherFilled() : weather()}
        />
        <SidebarItem
          label="Alerts"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.ALERTS)}
          onClick={handleSelect(MenuItemEnum.ALERTS)}
          badgeCount={alertsCount}
        >
          <WarningIcon
            color={iconColor(MenuItemEnum.ALERTS)}
            size={SIDEBAR_ICON_SIZE}
          />
        </SidebarItem>
        <SidebarItem
          label="Groups"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.GROUPS)}
          onClick={handleSelect(MenuItemEnum.GROUPS)}
          icon={people()}
        />
        <SidebarItem
          label="Plans"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.PLANS)}
          onClick={handleSelect(MenuItemEnum.PLANS)}
          icon={floatPlanMap()}
        />
        <SidebarItem
          label="You"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.YOU)}
          onClick={handleSelect(MenuItemEnum.YOU)}
          // Without a photo, children must be undefined so SidebarItem renders the icon
          icon={isActive(MenuItemEnum.YOU) ? customer() : customerOutlined()}
        >
          {member?.image ? (
            <Box
              borderRadius="50%"
              border="2px solid"
              borderColor={
                isActive(MenuItemEnum.YOU) ? 'primary' : 'transparent'
              }
              lineHeight={0}
            >
              <MemberAvatar
                memberAvatarId={member.image}
                width={SIDEBAR_AVATAR_SIZE}
                height={SIDEBAR_AVATAR_SIZE}
                addBorder={false}
                applyElevation={false}
              />
            </Box>
          ) : undefined}
        </SidebarItem>
      </Box>

      <Box mt="auto">
        <SidebarItem
          label="Settings"
          collapsed={collapsed}
          disabled={disabled}
          active={isActive(MenuItemEnum.SETTINGS)}
          onClick={handleSelect(MenuItemEnum.SETTINGS)}
        >
          <SettingsIcon
            color={iconColor(MenuItemEnum.SETTINGS)}
            size={SIDEBAR_ICON_SIZE}
          />
        </SidebarItem>
        <SidebarItem
          label="User guide"
          collapsed={collapsed}
          disabled={disabled}
          active={false}
          onClick={handleOpenUserGuide}
          icon={questionCircle()}
        />
      </Box>
    </SidebarWrapper>
  );
};

DesktopSidebar.defaultProps = {
  disabled: false,
};

export default DesktopSidebar;
